import React, { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Loader2, Search } from "lucide-react";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Typography from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import config from "@/config";
import {
  AdminFoundersKeys,
  AdminLicensee,
  getAdminFounders,
} from "@/features/founders/api/admin-founders";

function CopyableCell({ value, href }: { value: string; href?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
    [value],
  );

  return (
    <span className="group inline-flex items-center gap-1.5">
      <a
        href={href || value}
        target="_blank"
        rel="noopener noreferrer"
        className="truncate max-w-[300px] text-primary hover:underline"
      >
        {value}
      </a>
      <button
        onClick={handleCopy}
        className="shrink-0"
        title={`Copy: ${value}`}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 text-muted-foreground" />
        )}
      </button>
    </span>
  );
}

function getProfileUrl(founder: AdminLicensee): string | null {
  if (!founder.marketplaceProfile?._id) return null;
  return `${config.marketplaceUrl}/consultant/${founder.marketplaceProfile._id}`;
}

function getLinkedinUrl(founder: AdminLicensee): string | null {
  return founder.applicant?.linkedinUrl || null;
}

const CertifiedFounders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      AdminFoundersKeys.GET_FOUNDERS,
      "certified-founders-page",
      debouncedSearch,
    ],
    queryFn: () =>
      getAdminFounders(
        debouncedSearch,
        "firstName",
        "asc",
        0,
        200,
        true,
        "certified",
      ),
  });

  const founders = data?.items || [];

  if (error) {
    return (
      <DashboardLayout>
        <Typography size="h1">Certified Founders</Typography>
        <div className="text-red-600 mt-4">Failed to load certified founders.</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Typography size="h1">Certified Founders</Typography>

        <Card>
          <div className="p-4 border-b">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>LinkedIn</TableHead>
                  <TableHead>Marketplace Profile</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {founders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {debouncedSearch
                        ? `No certified founders found matching "${debouncedSearch}"`
                        : "No certified founders found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  founders.map((founder) => {
                    const profileUrl = getProfileUrl(founder);
                    const linkedinUrl = getLinkedinUrl(founder);

                    return (
                      <TableRow key={founder._id}>
                        <TableCell className="font-medium">
                          {founder.firstName} {founder.lastName}
                        </TableCell>
                        <TableCell>
                          <CopyableCell
                            value={founder.email}
                            href={`mailto:${founder.email}`}
                          />
                        </TableCell>
                        <TableCell>
                          {linkedinUrl ? (
                            <CopyableCell value={linkedinUrl} />
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {profileUrl ? (
                            <CopyableCell value={profileUrl} />
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              —
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {founders.length > 0 && (
            <div className="border-t px-4 py-3">
              <span className="text-sm text-muted-foreground">
                {founders.length} certified founder
                {founders.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CertifiedFounders;
